import cn from 'classnames';
import { Alert } from 'components/atoms/Alert';
import AIMessageItem from 'components/molecules/AIMessageItem';
import { last } from 'lodash';
import { useMemo, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { Message } from 'types/ai.types';
import { AIRoleEnum } from 'types/ai.types';
import { useAIChatContext } from './AIChat.context';
import AIChatDataShareModal from './AIChatDataShareModal';
import AIChatMessageGratification from './AIChatMessageGratification';
import AIChatRating from './AIChatRating';
import AIChatSource from './AIChatSource';

type AIChatProps = {
  isSmall?: boolean;
  initialMessage?: string;
  className?: string;
  lastMessageActions?: React.ReactNode;
  renderMessage?: (message: Message) => JSX.Element;
  showSource?: boolean;
  fullWidthMessages?: boolean;
  hideRating?: boolean;
};

const AIChat: FC<AIChatProps> = ({
  isSmall = false,
  initialMessage,
  className,
  lastMessageActions,
  renderMessage,
  showSource,
  fullWidthMessages = true,
  hideRating = false,
}) => {
  const {
    allMessages,
    conversation,
    currentTipId,
    isLoading,
    isStreaming,
    usage,
  } = useAIChatContext();
  const { t } = useTranslation();

  const lastMessage = useMemo(() => last(allMessages), [allMessages]);

  // Create a loading message when request is sent but not yet streaming
  const loadingMessage = useMemo(() => {
    if (isLoading && !isStreaming) {
      return {
        _id: 'loading',
        role: AIRoleEnum.assistant,
        content: '',
        parts: [],
      };
    }
    return null;
  }, [isLoading, isStreaming]);

  // Include loading message in the messages to render
  const messagesToRender = useMemo(() => {
    const messages = [...allMessages];
    if (loadingMessage) {
      messages.push(loadingMessage);
    }
    return messages;
  }, [allMessages, loadingMessage]);

  const lastMessageToRender = useMemo(
    () => last(messagesToRender),
    [messagesToRender],
  );

  return (
    <div className={cn('flex flex-col', className)}>
      {isSmall && (
        <AIChatDataShareModal
          showIcon
          title={t('ai_bot.data_share.helper.title')}
        />
      )}

      {showSource && conversation?._id ? <AIChatSource /> : null}

      {!conversation && currentTipId ? (
        <AIChatMessageGratification gratificationId={currentTipId} />
      ) : null}

      {initialMessage && (
        <AIMessageItem
          isSmall={isSmall}
          isFullWidth={fullWidthMessages}
          message={{
            _id: 'initial',
            role: AIRoleEnum.assistant,
            content: initialMessage,
            parts: [{ type: 'text', content: initialMessage }],
          }}
        />
      )}

      {messagesToRender?.map((message) => (
        <AIMessageItem
          key={message._id}
          isSmall={isSmall}
          isFullWidth={fullWidthMessages}
          renderMessage={renderMessage}
          message={message}
          isLastAssistantMessage={
            lastMessageToRender?._id === message._id &&
            lastMessageToRender.role !== 'user'
          }
        />
      ))}
      {!isLoading &&
        lastMessage &&
        lastMessage.role !== 'user' &&
        lastMessage._id !== 'stream' &&
        lastMessageActions}

      {usage?.reachedMaxCount && conversation?.status === 'processed' ? (
        <Alert
          type="warning"
          description={t('ai_bot.message_count_warning', {
            limit: usage?.conversation_message_limit,
          })}
          className="mt-3"
        />
      ) : null}

      {hideRating ? null : <AIChatRating isSmall={isSmall} />}
    </div>
  );
};

export default AIChat;
